import zod from "zod";
import { Answerer } from "../../../api/generated";
import { EMAIL_REGEX } from "../../../libs/constants";

/**
 * 回答者の基本スキーマ
 */
export const answererBaseSchema = zod.object({
    name: zod
        .string()
        .min(1, { message: "あなたのニックネームは入力必須です。" }),
    relationship: zod.enum(["father", "mother", "others"], {
        message: "いづれかを選択してください。",
    }),
    birthday: zod
        .string()
        .refine((value) => value === "" || /^\d{8}$/.test(value), {
            message: "日付は半角数字8桁で入力してください。",
        })
        .optional(),
    receiverNickname: zod
        .string()
        .min(1, { message: "あなたのニックネームは入力必須です。" }),
    email: zod.string(),
    questionSendType: zod.union([zod.literal("Email"), zod.literal("Line")]),
    qmailFrequency: zod
        .array(
            zod.union([
                zod.literal("Sunday"),
                zod.literal("Monday"),
                zod.literal("Tuesday"),
                zod.literal("Wednesday"),
                zod.literal("Thursday"),
                zod.literal("Friday"),
                zod.literal("Saturday"),
            ])
        )
        .min(1, { message: "最低でも１つは選択してください。" }),
});

/**
 * receiverNickname専用のスキーマ（ReceiverNicknamePage用）
 */
export const receiverNicknameSchema = answererBaseSchema.pick({
    receiverNickname: true,
});

export type ReceiverNicknameInput = zod.infer<typeof receiverNicknameSchema>;

/**
 * 共通のバリデーションスキーマを構築
 * @param answerers - 既存の回答者リスト
 * @param excludeAnswererId - 更新時に除外する回答者ID（自分自身を除外）
 */
const buildAnswererValidationSchema = (
    answerers: Answerer[],
    excludeAnswererId?: string
) => {
    return answererBaseSchema
        .refine(
            (data) => {
                // Email選択時のみemail必須
                if (data.questionSendType === "Email") {
                    return data.email.length > 0;
                }
                return true;
            },
            { message: "メールアドレスは入力必須です。", path: ["email"] }
        )
        .refine(
            (data) => {
                // Emailが選択されている場合は、メールアドレスの形式をチェック
                if (data.email) {
                    return data.email.match(EMAIL_REGEX);
                }
                return true;
            },
            { message: "メールアドレスが不正です。", path: ["email"] }
        )
        .refine(
            (data) => {
                // Emailが選択されている場合は、重複チェック
                if (data.email) {
                    // 自分自身を除外して重複チェック
                    const otherAnswerers = excludeAnswererId
                        ? answerers.filter(
                              (x) => x.answererId !== excludeAnswererId
                          )
                        : answerers;

                    return !otherAnswerers
                        .map((x) => x.email)
                        .some((email) => email === data.email);
                }
                return true;
            },
            {
                message:
                    "このメールアドレスは、既に別の相手へのメールアドレスとして登録されています。別のメールアドレスのご利用をご検討ください。",
                path: ["email"],
            }
        );
};

/**
 * 新規作成用のスキーマ
 * - email必須（Emailが選択されている場合）
 * - 重複チェック
 */
export const createAnswererInputSchema = (answerers: Answerer[]) => {
    return buildAnswererValidationSchema(answerers);
};

/**
 * 更新用のスキーマ
 * - email必須（Emailが選択されている場合）
 * - 重複チェック（自分自身は除外）
 */
export const updateAnswererInputSchema = (
    answerers: Answerer[],
    answererId: string
) => {
    return buildAnswererValidationSchema(answerers, answererId);
};

export type CreateAnswererInput = zod.infer<typeof answererBaseSchema>;
export type UpdateAnswererInput = zod.infer<typeof answererBaseSchema>;
